/**
 * Hook for fetching and caching global statistics
 */

import { useState, useEffect, useCallback } from 'react';
import type { GlobalStats } from '@doyoulikethis/shared';
import { getGlobalStats } from '../lib/api';

interface UseGlobalStatsResult {
  stats: GlobalStats | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// Cache global stats for 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

let cachedStats: GlobalStats | null = null;
let cacheTimestamp = 0;

export function useGlobalStats(): UseGlobalStatsResult {
  const [stats, setStats] = useState<GlobalStats | null>(cachedStats);
  const [loading, setLoading] = useState(!cachedStats);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async (forceRefresh = false) => {
    // Check cache
    const now = Date.now();
    if (!forceRefresh && cachedStats && now - cacheTimestamp < CACHE_DURATION_MS) {
      setStats(cachedStats);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getGlobalStats();
      if (data) {
        cachedStats = data;
        cacheTimestamp = now;
        setStats(data);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const refresh = useCallback(async () => {
    await fetchStats(true);
  }, [fetchStats]);

  return { stats, loading, error, refresh };
}
